/**
 * iter549 + iter551 — Dashboard layout shared context.
 *
 * Wraps the entire dashboard so EVERY consumer of `useDashboardLayout`
 * reads from the same in-memory state.
 *
 * iter551 additions:
 *   - `saving` — true while a PUT to /api/preferences is in-flight
 *   - `lastSavedAt` — Date of the last successful save (for green
 *     "✓ Всички промени запазени" indicator)
 *   - `editMode` / `setEditMode` — on-dashboard inline edit mode
 *     (shows drag handles + red X buttons on every widget directly on
 *      the dashboard, not just inside the drawer)
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "nutramilo:dashboard_layout";

function loadLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function saveLocal(layout) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(layout));
  } catch {
    /* ignore */
  }
}

const DashboardLayoutContext = createContext(null);

export function DashboardLayoutProvider({ children }) {
  const [layout, setLayout] = useState(() => loadLocal() || { order: [], hidden: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/preferences`, { withCredentials: true });
        if (cancelled) return;
        const next = {
          order: Array.isArray(data?.dashboard_layout?.order) ? data.dashboard_layout.order : [],
          hidden: Array.isArray(data?.dashboard_layout?.hidden) ? data.dashboard_layout.hidden : [],
        };
        setLayout(next);
        saveLocal(next);
      } catch {
        /* keep cached / default */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scheduleSave = useCallback((next) => {
    saveLocal(next);
    setSaving(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(
          `${API}/preferences`,
          { dashboard_layout: next },
          { withCredentials: true }
        );
        setLastSavedAt(new Date());
      } catch {
        /* server rejected — keep local */
      } finally {
        setSaving(false);
      }
    }, 400);
  }, []);

  const setHidden = useCallback((id, hidden) => {
    setLayout((prev) => {
      const set = new Set(prev.hidden || []);
      if (hidden) set.add(id);
      else set.delete(id);
      const next = { order: prev.order || [], hidden: Array.from(set) };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const setOrder = useCallback((orderedIds) => {
    setLayout((prev) => {
      const next = { order: orderedIds, hidden: prev.hidden || [] };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const resetLayout = useCallback(() => {
    const next = { order: [], hidden: [] };
    setLayout(next);
    scheduleSave(next);
  }, [scheduleSave]);

  const value = {
    layout,
    setHidden,
    setOrder,
    resetLayout,
    loading,
    saving,
    lastSavedAt,
    editMode,
    setEditMode,
  };
  return (
    <DashboardLayoutContext.Provider value={value}>
      {children}
    </DashboardLayoutContext.Provider>
  );
}

export function useDashboardLayoutContext() {
  const ctx = useContext(DashboardLayoutContext);
  if (ctx) return ctx;
  return {
    layout: { order: [], hidden: [] },
    setHidden: () => {},
    setOrder: () => {},
    resetLayout: () => {},
    loading: false,
    saving: false,
    lastSavedAt: null,
    editMode: false,
    setEditMode: () => {},
  };
}
